import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import useElementToPdf from '../../utils/useElementToPdf';
import { 
  Activity, 
  Printer, 
  Filter, 
  Calendar,
  User,
  Edit2,
  Trash2,
  Plus,
  FileText,
  DollarSign,
  Package,
  RefreshCw,
  Clock,
  Database,
  AlertTriangle,
  CheckCircle,
  Languages,
  Globe
} from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { safeFormat, safeParseDate } from '../../utils/dateUtils';

const ActivityLog = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [retentionStats, setRetentionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [filters, setFilters] = useState({
    section: '',
    action: '',
    startDate: '',
    endDate: ''
  });
  const printRef = useRef();
  
  // Language settings for print
  const [printLanguage, setPrintLanguage] = useState(language);
  const [bilingualPrint, setBilingualPrint] = useState(false);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  
  const isArabic = language === 'ar';
  
  // Translations
  const TEXTS = {
    ar: {
      activityLog: 'سجل النشاطات',
      trackChanges: 'تتبع جميع التعديلات والحذوفات في النظام',
      refresh: 'تحديث',
      printReport: 'طباعة التقرير',
      filterResults: 'تصفية النتائج',
      section: 'القسم',
      all: 'الكل',
      expenses: 'المصروفات',
      revenues: 'الإيرادات',
      advance: 'السلفة',
      inventory: 'المخزون',
      operationType: 'نوع العملية',
      create: 'إضافة',
      update: 'تعديل',
      delete: 'حذف',
      fromDate: 'من تاريخ',
      toDate: 'إلى تاريخ',
      filter: 'تصفية',
      clear: 'مسح',
      totalActivities: 'إجمالي النشاطات',
      additions: 'إضافات',
      modifications: 'تعديلات',
      deletions: 'حذوفات',
      user: 'المستخدم',
      action: 'العملية',
      description: 'الوصف',
      date: 'التاريخ',
      time: 'الوقت',
      noActivities: 'لا توجد نشاطات مسجلة',
      noAccess: 'ليس لديك صلاحية للوصول إلى هذه الصفحة',
      printOptions: 'خيارات الطباعة',
      reportLanguage: 'لغة التقرير',
      bilingual: 'ثنائي اللغة',
      print: 'طباعة',
      cancel: 'إلغاء',
      generatedOn: 'تم إنشاء التقرير في'
    },
    en: {
      activityLog: 'Activity Log',
      trackChanges: 'Track all modifications and deletions in the system',
      refresh: 'Refresh',
      printReport: 'Print Report',
      filterResults: 'Filter Results',
      section: 'Section',
      all: 'All',
      expenses: 'Expenses',
      revenues: 'Revenues',
      advance: 'Advance',
      inventory: 'Inventory',
      operationType: 'Operation Type',
      create: 'Create',
      update: 'Update',
      delete: 'Delete',
      fromDate: 'From Date',
      toDate: 'To Date',
      filter: 'Filter',
      clear: 'Clear',
      totalActivities: 'Total Activities',
      additions: 'Additions',
      modifications: 'Modifications',
      deletions: 'Deletions',
      user: 'User',
      action: 'Action',
      description: 'Description',
      date: 'Date',
      time: 'Time',
      noActivities: 'No activities recorded',
      noAccess: 'You do not have permission to access this page',
      printOptions: 'Print Options',
      reportLanguage: 'Report Language',
      bilingual: 'Bilingual',
      print: 'Print',
      cancel: 'Cancel',
      generatedOn: 'Generated on'
    }
  };
  
  const getText = (key, lang = printLanguage) => {
    return TEXTS[lang]?.[key] || TEXTS['ar'][key];
  };
  
  const getBilingualText = (key) => {
    if (bilingualPrint) {
      return `${TEXTS['ar'][key]} / ${TEXTS['en'][key]}`;
    }
    return getText(key);
  };

  // react-to-print hook
  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: getBilingualText('activityLog'),
    pageStyle: `
      @page { size: A4 landscape; margin: 15mm; }
      @media print {
        body { 
          -webkit-print-color-adjust: exact !important; 
          print-color-adjust: exact !important;
          direction: ${bilingualPrint ? 'rtl' : (printLanguage === 'ar' ? 'rtl' : 'ltr')};
          font-family: 'Segoe UI', Tahoma, sans-serif;
        }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 10px; text-align: ${bilingualPrint || printLanguage === 'ar' ? 'right' : 'left'}; }
        th { background: #f5f5f5; }
        .print\\:hidden { display: none !important; }
      }
    `
  });
  
  const triggerPrint = () => {
    setShowPrintOptions(false);
    setTimeout(() => handlePrint(), 100);
  };

  useEffect(() => {
    fetchLogs();
    fetchRetentionStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.section) params.append('section', filters.section);
      if (filters.action) params.append('action', filters.action);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const [logsRes, summaryRes] = await Promise.all([
        api.get(`/activity-logs?${params.toString()}`),
        api.get(`/activity-logs/summary?${params.toString()}`)
      ]);
      
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('فشل تحميل سجل النشاطات');
    } finally {
      setLoading(false);
    }
  };

  const fetchRetentionStats = async () => {
    try {
      const res = await api.get('/activity-logs/stats');
      setRetentionStats(res.data);
    } catch (error) {
      console.error('Failed to fetch retention stats');
    }
  };

  const handleManualCleanup = async () => {
    if (!window.confirm('هل أنت متأكد من حذف السجلات الأقدم من شهرين؟ هذا الإجراء لا يمكن التراجع عنه.')) return;
    
    try {
      setCleanupLoading(true);
      const res = await api.post('/activity-logs/cleanup');
      toast.success(res.data.message);
      fetchLogs();
      fetchRetentionStats();
    } catch (error) {
      toast.error('فشل تنظيف السجلات');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleFilter = () => {
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      section: '',
      action: '',
      startDate: '',
      endDate: ''
    });
    setTimeout(fetchLogs, 100);
  };

  const getSectionName = (section, lang = printLanguage) => {
    const sections = {
      ar: {
        expenses: 'المصروفات',
        revenues: 'الإيرادات',
        advance: 'السلفة',
        inventory: 'المخزون',
        barbers: 'الحلاقين',
        appointments: 'المواعيد',
        salaries: 'الرواتب'
      },
      en: {
        expenses: 'Expenses',
        revenues: 'Revenues',
        advance: 'Advance',
        inventory: 'Inventory',
        barbers: 'Barbers',
        appointments: 'Appointments',
        salaries: 'Salaries'
      }
    };
    if (bilingualPrint) {
      return `${sections['ar'][section] || section} / ${sections['en'][section] || section}`;
    }
    return sections[lang]?.[section] || section;
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="w-4 h-4 text-green-500" />;
      case 'update': return <Edit2 className="w-4 h-4 text-amber-500" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActionText = (action, lang = printLanguage) => {
    const actions = {
      ar: {
        create: 'إضافة',
        update: 'تعديل',
        delete: 'حذف'
      },
      en: {
        create: 'Create',
        update: 'Update',
        delete: 'Delete'
      }
    };
    if (bilingualPrint) {
      return `${actions['ar'][action] || action} / ${actions['en'][action] || action}`;
    }
    return actions[lang]?.[action] || action;
  };

  const getSectionIcon = (section) => {
    switch (section) {
      case 'expenses': return <FileText className="w-4 h-4" />;
      case 'revenues': return <DollarSign className="w-4 h-4" />;
      case 'inventory': return <Package className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">{isArabic ? 'ليس لديك صلاحية للوصول إلى هذه الصفحة' : 'You do not have permission to access this page'}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Print Options Modal */}
      {showPrintOptions && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-card p-6 rounded-xl max-w-sm w-full mx-4 space-y-4">
            <div className="flex items-center gap-2 text-lg font-bold">
              <Globe className="w-5 h-5 text-primary" />
              {isArabic ? 'خيارات الطباعة' : 'Print Options'}
            </div>
            
            {/* Language Selection */}
            <div className="space-y-3 border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {isArabic ? 'لغة التقرير' : 'Report Language'}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setPrintLanguage('ar')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      printLanguage === 'ar' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    عربي
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintLanguage('en')}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      printLanguage === 'en' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                  >
                    English
                  </button>
                </div>
              </div>
              
              {/* Bilingual Toggle */}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">{isArabic ? 'ثنائي اللغة' : 'Bilingual'}</span>
                </div>
                <Switch
                  checked={bilingualPrint}
                  onCheckedChange={setBilingualPrint}
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={triggerPrint} className="flex-1">
                <Printer className="w-4 h-4 me-2" />
                {isArabic ? 'تنزيل PDF' : 'Download PDF'}
              </Button>
              <Button onClick={() => setShowPrintOptions(false)} variant="outline">
                {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Printable Content */}
      <div ref={printRef} dir={bilingualPrint ? 'rtl' : (printLanguage === 'ar' ? 'rtl' : 'ltr')}>
      
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Activity className="w-8 h-8 text-primary" />
            {isArabic ? 'سجل النشاطات' : 'Activity Log'}
          </h1>
          <p className="text-muted-foreground">{isArabic ? 'تتبع جميع التعديلات والحذوفات في النظام' : 'Track all modifications and deletions'}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchLogs} variant="outline" className="border-primary/50">
            <RefreshCw className="w-4 h-4 me-2" />
            {isArabic ? 'تحديث' : 'Refresh'}
          </Button>
          <Button onClick={() => setShowPrintOptions(true)} className="bg-primary text-primary-foreground">
            <Printer className="w-4 h-4 me-2" />
            {isArabic ? 'طباعة التقرير' : 'Print Report'}
          </Button>
        </div>
      </div>
      
      {/* Print Header */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-2xl font-bold">KE Gents Saloon</h1>
        <h2 className="text-lg mt-1">{getBilingualText('activityLog')}</h2>
        <p className="text-sm text-gray-500">{getBilingualText('generatedOn')}: {new Date().toLocaleDateString(printLanguage === 'ar' ? 'ar-AE' : 'en-US')}</p>
      </div>

      {/* Filters */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">تصفية النتائج</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Select value={filters.section || "all"} onValueChange={(v) => setFilters({...filters, section: v === "all" ? "" : v})}>
            <SelectTrigger className="bg-secondary/50 border-white/10">
              <SelectValue placeholder="القسم" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="expenses">المصروفات</SelectItem>
              <SelectItem value="revenues">الإيرادات</SelectItem>
              <SelectItem value="advance">السلفة</SelectItem>
              <SelectItem value="inventory">المخزون</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filters.action || "all"} onValueChange={(v) => setFilters({...filters, action: v === "all" ? "" : v})}>
            <SelectTrigger className="bg-secondary/50 border-white/10">
              <SelectValue placeholder="نوع العملية" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="create">إضافة</SelectItem>
              <SelectItem value="update">تعديل</SelectItem>
              <SelectItem value="delete">حذف</SelectItem>
            </SelectContent>
          </Select>
          
          <Input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({...filters, startDate: e.target.value})}
            placeholder="من تاريخ"
            className="bg-secondary/50 border-white/10"
          />
          
          <Input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({...filters, endDate: e.target.value})}
            placeholder="إلى تاريخ"
            className="bg-secondary/50 border-white/10"
          />
          
          <div className="flex gap-2">
            <Button onClick={handleFilter} className="flex-1 bg-primary text-primary-foreground">
              تصفية
            </Button>
            <Button onClick={handleClearFilters} variant="outline" className="border-white/10">
              مسح
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 text-center">
            <Activity className="w-8 h-8 text-primary mx-auto mb-2" />
            <p className="text-3xl font-bold text-primary">{summary.total_activities}</p>
            <p className="text-sm text-muted-foreground">إجمالي النشاطات</p>
          </div>
          <div className="bg-card border border-green-500/20 rounded-xl shadow-2xl p-6 text-center">
            <Plus className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-500">{summary.by_action?.create || 0}</p>
            <p className="text-sm text-muted-foreground">إضافات</p>
          </div>
          <div className="bg-card border border-amber-500/20 rounded-xl shadow-2xl p-6 text-center">
            <Edit2 className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-amber-500">{summary.by_action?.update || 0}</p>
            <p className="text-sm text-muted-foreground">تعديلات</p>
          </div>
          <div className="bg-card border border-red-500/20 rounded-xl shadow-2xl p-6 text-center">
            <Trash2 className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-500">{summary.by_action?.delete || 0}</p>
            <p className="text-sm text-muted-foreground">حذوفات</p>
          </div>
        </div>
      )}

      {/* Retention Management Section */}
      {retentionStats && (
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              <h2 className="font-semibold">إدارة الاحتفاظ بالسجلات</h2>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>التنظيف التلقائي: {retentionStats.next_cleanup}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Retention Period Info */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="font-medium">فترة الاحتفاظ</span>
              </div>
              <p className="text-2xl font-bold text-primary">{retentionStats.retention_period_days} يوم</p>
              <p className="text-xs text-muted-foreground mt-1">
                السجلات الأقدم من شهرين تُحذف تلقائياً
              </p>
            </div>
            
            {/* Total Logs */}
            <div className="bg-secondary/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-blue-500" />
                <span className="font-medium">إجمالي السجلات</span>
              </div>
              <p className="text-2xl font-bold text-blue-500">{retentionStats.total_logs?.total || 0}</p>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <p>• النشاطات: {retentionStats.total_logs?.activity_logs || 0}</p>
                <p>• الصلاحيات: {retentionStats.total_logs?.permission_logs || 0}</p>
                <p>• الزيارات: {retentionStats.total_logs?.visits || 0}</p>
              </div>
            </div>
            
            {/* Logs to Delete */}
            <div className={`rounded-lg p-4 ${
              retentionStats.logs_to_delete?.total > 0 
                ? 'bg-amber-500/10 border border-amber-500/20' 
                : 'bg-green-500/10 border border-green-500/20'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {retentionStats.logs_to_delete?.total > 0 ? (
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
                <span className="font-medium">سجلات قديمة</span>
              </div>
              <p className={`text-2xl font-bold ${
                retentionStats.logs_to_delete?.total > 0 ? 'text-amber-500' : 'text-green-500'
              }`}>
                {retentionStats.logs_to_delete?.total || 0}
              </p>
              {retentionStats.logs_to_delete?.total > 0 ? (
                <p className="text-xs text-amber-500 mt-1">
                  ستُحذف في التنظيف القادم
                </p>
              ) : (
                <p className="text-xs text-green-500 mt-1">
                  لا توجد سجلات قديمة للحذف
                </p>
              )}
            </div>
          </div>
          
          {/* Manual Cleanup Button */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <div className="text-sm text-muted-foreground">
              {retentionStats.last_cleanup ? (
                <span>آخر تنظيف: {safeFormat(retentionStats.last_cleanup, 'yyyy-MM-dd HH:mm', { fallback: '-' })}</span>
              ) : (
                <span>لم يتم إجراء تنظيف سابق</span>
              )}
            </div>
            <Button 
              onClick={handleManualCleanup}
              disabled={cleanupLoading || retentionStats.logs_to_delete?.total === 0}
              variant="outline"
              className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
            >
              {cleanupLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-amber-500 me-2"></div>
              ) : (
                <Trash2 className="w-4 h-4 me-2" />
              )}
              تنظيف يدوي الآن
            </Button>
          </div>
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">التاريخ والوقت</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">المستخدم</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الدور</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">العملية</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">القسم</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">التفاصيل</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                    لا توجد نشاطات مسجلة
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        {safeFormat(log.created_at, 'yyyy-MM-dd HH:mm', { fallback: '-' })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-primary" />
                        {log.user_name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        log.user_role === 'admin' 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-blue-500/20 text-blue-500'
                      }`}>
                        {log.user_role === 'admin' ? (isArabic ? 'مدير' : 'Admin') : (isArabic ? 'حلاق' : 'Barber')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getActionIcon(log.action)}
                        <span className={`font-medium ${
                          log.action === 'create' ? 'text-green-500' :
                          log.action === 'update' ? 'text-amber-500' :
                          'text-red-500'
                        }`}>
                          {getActionText(log.action)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getSectionIcon(log.section)}
                        {getSectionName(log.section)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground max-w-xs truncate">
                      {log.details || log.item_description || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      </div>{/* End printRef container */}
    </div>
  );
};

export default ActivityLog;
